import { computed, onUnmounted, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue';

interface RowMenuPositionOptions {
  width: number;
  height: MaybeRefOrGetter<number>;
  gap?: number;
  viewportMargin?: number;
}

/**
 * Anchors a row menu to the viewport instead of to its cell. `DefaultTable` wraps the table in
 * `overflow-x: auto`, and CSS turns the `overflow-y: visible` next to it into `auto`, so a panel
 * positioned inside the cell is clipped by the card and the rows at the bottom of the page open a
 * menu nobody can read.
 */
export function useRowMenuPosition(isOpen: Ref<boolean>, options: RowMenuPositionOptions) {
  const { width, height, gap = 8, viewportMargin = 8 } = options;

  const triggerRef = ref<HTMLElement | null>(null);
  const top = ref(0);
  const left = ref(0);

  const menuStyle = computed(() => ({
    '--row-menu-top': `${top.value}px`,
    '--row-menu-left': `${left.value}px`,
  }));

  const updatePosition = () => {
    const rect = triggerRef.value?.getBoundingClientRect();
    if (!rect) return;

    const menuHeight = toValue(height);
    const fitsBelow = rect.bottom + gap + menuHeight <= window.innerHeight - viewportMargin;

    top.value = fitsBelow
      ? rect.bottom + gap
      : Math.max(viewportMargin, rect.top - gap - menuHeight);
    left.value = Math.max(viewportMargin, rect.right - width);
  };

  const stopTracking = () => {
    window.removeEventListener('scroll', updatePosition, true);
    window.removeEventListener('resize', updatePosition);
  };

  watch(isOpen, (open) => {
    if (!open) {
      stopTracking();
      return;
    }

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
  });

  onUnmounted(stopTracking);

  return { triggerRef, menuStyle, updatePosition };
}
